/**
 * useGameMedia - Composable for managing video/audio in the game board
 *
 * Extracted from GameBoard.vue to reduce component complexity.
 * Handles:
 * - Video element refs and stream binding
 * - Remote video detection
 * - Video/audio toggle handlers
 * - Remote stream watcher
 *
 * Usage:
 * ```typescript
 * const media = useGameMedia()
 * // ... define player computed props ...
 * media.setup({ partnerPlayer, opponent1Player, opponent2Player })
 * ```
 */

import { ref, computed, watch, nextTick, shallowRef, type ComputedRef } from 'vue'
import { useVideo } from './useVideo'
import { useWebRTC } from './useWebRTC'
import { useAudio } from './useAudio'
import { useSocket } from './useSocket'
import { Logger, LogCategory } from '@/utils/Logger.class'

// Generic player type - only requires id for video binding
interface PlayerWithId {
  id: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PlayerComputed = ComputedRef<(PlayerWithId & Record<string, any>) | undefined>

interface UseGameMediaOptions {
  partnerPlayer: PlayerComputed
  opponent1Player: PlayerComputed
  opponent2Player: PlayerComputed
}

export function useGameMedia() {
  // Player refs - set via setup()
  const partnerPlayer = shallowRef<PlayerComputed | null>(null)
  const opponent1Player = shallowRef<PlayerComputed | null>(null)
  const opponent2Player = shallowRef<PlayerComputed | null>(null)

  // Composables
  const video = useVideo()
  const webrtc = useWebRTC()
  const audio = useAudio()
  const socket = useSocket()

  // Video element refs
  const localVideoRef = ref<HTMLVideoElement | null>(null)
  const partnerVideoRef = ref<HTMLVideoElement | null>(null)
  const opponent1VideoRef = ref<HTMLVideoElement | null>(null)
  const opponent2VideoRef = ref<HTMLVideoElement | null>(null)

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /**
   * Check if a MediaStream has active video tracks
   */
  function streamHasVideo(stream: MediaStream | undefined | null): boolean {
    if (!stream) return false
    const videoTracks = stream.getVideoTracks()
    // Check if we have at least one video track (regardless of enabled/active state)
    // Tracks can be temporarily disabled when tab is hidden but still recoverable
    return videoTracks.length > 0
  }

  // ============================================================================
  // Computed Properties - Remote Video Availability
  // ============================================================================

  /**
   * Check if partner has an active video stream
   */
  const partnerHasVideo = computed(() => {
    const player = partnerPlayer.value?.value
    const playerId = player?.id
    if (!playerId) return false
    const streams = webrtc.remoteStreams.value
    const stream = streams.get(playerId)
    const videoTracks = stream?.getVideoTracks() || []
    const result = streamHasVideo(stream)
    console.log('[VIDEO-COMPUTED] partnerHasVideo', {
      playerId,
      hasStream: !!stream,
      streamActive: stream?.active,
      videoTrackCount: videoTracks.length,
      trackStates: videoTracks.map((t) => ({
        enabled: t.enabled,
        readyState: t.readyState,
        muted: t.muted
      })),
      result
    })
    return result
  })

  /**
   * Check if opponent 1 has an active video stream
   */
  const opponent1HasVideo = computed(() => {
    const player = opponent1Player.value?.value
    const playerId = player?.id
    if (!playerId) return false
    const streams = webrtc.remoteStreams.value
    const stream = streams.get(playerId)
    const videoTracks = stream?.getVideoTracks() || []
    const result = streamHasVideo(stream)
    console.log('[VIDEO-COMPUTED] opponent1HasVideo', {
      playerId,
      hasStream: !!stream,
      streamActive: stream?.active,
      videoTrackCount: videoTracks.length,
      trackStates: videoTracks.map((t) => ({
        enabled: t.enabled,
        readyState: t.readyState,
        muted: t.muted
      })),
      result
    })
    return result
  })

  /**
   * Check if opponent 2 has an active video stream
   */
  const opponent2HasVideo = computed(() => {
    const player = opponent2Player.value?.value
    const playerId = player?.id
    if (!playerId) return false
    const streams = webrtc.remoteStreams.value
    const stream = streams.get(playerId)
    const videoTracks = stream?.getVideoTracks() || []
    const result = streamHasVideo(stream)
    console.log('[VIDEO-COMPUTED] opponent2HasVideo', {
      playerId,
      hasStream: !!stream,
      streamActive: stream?.active,
      videoTrackCount: videoTracks.length,
      trackStates: videoTracks.map((t) => ({
        enabled: t.enabled,
        readyState: t.readyState,
        muted: t.muted
      })),
      result
    })
    return result
  })

  // ============================================================================
  // Handler Functions
  // ============================================================================

  /**
   * Toggle local video on/off
   */
  async function handleVideoToggle(): Promise<void> {
    Logger.info(LogCategory.VIDEO, 'Video toggle clicked', {
      currentState: video.videoEnabled.value,
      hasStream: !!video.localStream.value
    })

    const success = await video.toggleVideo()

    Logger.info(LogCategory.VIDEO, 'Video toggle result', {
      success,
      newState: video.videoEnabled.value
    })

    // Attach stream to video element when enabled
    if (success && video.localStream.value && localVideoRef.value) {
      localVideoRef.value.srcObject = video.localStream.value
    }

    // Emit state change to other players (for WebRTC)
    if (socket.isConnected()) {
      Logger.debug(LogCategory.VIDEO, 'Emitting video_state_changed', {
        video_enabled: video.videoEnabled.value,
        audio_enabled: video.audioEnabled.value
      })
      socket.socketEmit('video_state_changed', {
        video_enabled: video.videoEnabled.value,
        audio_enabled: video.audioEnabled.value
      })
    }
  }

  /**
   * Toggle local audio (microphone) on/off
   */
  async function handleAudioToggle(): Promise<void> {
    Logger.info(LogCategory.AUDIO, 'Audio toggle clicked', {
      currentState: video.audioEnabled.value
    })

    await video.toggleAudio()

    Logger.info(LogCategory.AUDIO, 'Audio toggle result', {
      newState: video.audioEnabled.value
    })

    // Emit state change to other players (for WebRTC)
    if (socket.isConnected()) {
      Logger.debug(LogCategory.AUDIO, 'Emitting video_state_changed', {
        video_enabled: video.videoEnabled.value,
        audio_enabled: video.audioEnabled.value
      })
      socket.socketEmit('video_state_changed', {
        video_enabled: video.videoEnabled.value,
        audio_enabled: video.audioEnabled.value
      })
    }
  }

  /**
   * Resume audio playback when blocked by browser autoplay policy
   */
  async function handleResumeAudio(): Promise<void> {
    await audio.resumeBlockedAudio()
  }

  /**
   * Check if a remote player has an active video stream
   */
  function hasRemoteVideo(playerId: string | undefined): boolean {
    if (!playerId) return false
    const stream = webrtc.getRemoteStream(playerId)
    const hasVideo =
      stream !== null && stream.active && stream.getVideoTracks().length > 0

    // Log occasionally to avoid spam (only when streams exist)
    if (webrtc.remoteStreams.value.size > 0) {
      console.log('[VIDEO-CHECK] hasRemoteVideo', {
        playerId,
        hasStream: !!stream,
        streamActive: stream?.active,
        videoTracks: stream?.getVideoTracks().length ?? 0,
        hasVideo
      })
    }

    return hasVideo
  }

  /**
   * Bind a remote video stream to a video element
   */
  function bindRemoteVideo(
    playerId: string | undefined,
    videoEl: HTMLVideoElement | null
  ): void {
    if (!playerId || !videoEl) return
    const stream = webrtc.getRemoteStream(playerId)

    console.log('[VIDEO-BIND] bindRemoteVideo called', {
      playerId,
      hasStream: !!stream,
      streamActive: stream?.active,
      videoTracks: stream?.getVideoTracks().length ?? 0,
      audioTracks: stream?.getAudioTracks().length ?? 0
    })

    // Bind if stream has video tracks, regardless of stream.active state
    if (stream && stream.getVideoTracks().length > 0) {
      videoEl.srcObject = stream
      Logger.debug(LogCategory.VIDEO, 'Remote video bound to element', {
        playerId,
        streamId: stream.id
      })
      console.log('[VIDEO-BIND] Successfully bound stream to video element', {
        playerId
      })
    } else {
      console.log('[VIDEO-BIND] Skipped binding - no video tracks', {
        playerId
      })
    }
  }

  /**
   * Handler for video element mount - binds stream when element becomes available
   */
  function onVideoMounted(
    playerId: string | undefined,
    videoEl: HTMLVideoElement | null
  ): void {
    console.log('[VIDEO-MOUNT] Video element mounted', {
      playerId,
      hasVideoEl: !!videoEl
    })
    if (!playerId || !videoEl) return

    const stream = webrtc.getRemoteStream(playerId)
    // Bind if stream has video tracks, regardless of stream.active state
    if (stream && stream.getVideoTracks().length > 0) {
      console.log('[VIDEO-MOUNT] Binding stream to newly mounted video', {
        playerId,
        streamId: stream.id,
        streamActive: stream.active,
        videoTracks: stream.getVideoTracks().length
      })
      videoEl.srcObject = stream
    } else {
      console.log('[VIDEO-MOUNT] No stream or no video tracks for player', {
        playerId,
        hasStream: !!stream,
        videoTracks: stream?.getVideoTracks().length ?? 0
      })
    }
  }

  // ============================================================================
  // Watcher - Remote Stream Binding
  // ============================================================================

  /**
   * Watch for remote video streams and bind to video elements
   * When remoteStreams changes, update the video srcObject bindings
   */
  function setupRemoteStreamWatcher(): void {
    watch(
      () => webrtc.remoteStreams.value,
      () => {
        const partner = partnerPlayer.value?.value
        const opp1 = opponent1Player.value?.value
        const opp2 = opponent2Player.value?.value

        console.log('[VIDEO-18] remoteStreams watcher fired', {
          streamCount: webrtc.remoteStreams.value.size,
          streamKeys: Array.from(webrtc.remoteStreams.value.keys()),
          partnerPlayerId: partner?.id,
          opponent1PlayerId: opp1?.id,
          opponent2PlayerId: opp2?.id
        })

        // Use nextTick to ensure video elements are rendered
        nextTick(() => {
          // Bind partner video
          if (partner?.id) {
            console.log('[VIDEO-18B] Attempting to bind partner video', {
              partnerId: partner.id,
              hasVideoEl: !!partnerVideoRef.value,
              streamExists: webrtc.remoteStreams.value.has(partner.id)
            })
            bindRemoteVideo(partner.id, partnerVideoRef.value)
          }
          // Bind opponent 1 video
          if (opp1?.id) {
            console.log('[VIDEO-18C] Attempting to bind opponent1 video', {
              opponent1Id: opp1.id,
              hasVideoEl: !!opponent1VideoRef.value,
              streamExists: webrtc.remoteStreams.value.has(opp1.id)
            })
            bindRemoteVideo(opp1.id, opponent1VideoRef.value)
          }
          // Bind opponent 2 video
          if (opp2?.id) {
            console.log('[VIDEO-18D] Attempting to bind opponent2 video', {
              opponent2Id: opp2.id,
              hasVideoEl: !!opponent2VideoRef.value,
              streamExists: webrtc.remoteStreams.value.has(opp2.id)
            })
            bindRemoteVideo(opp2.id, opponent2VideoRef.value)
          }

          Logger.debug(LogCategory.VIDEO, 'Remote streams updated', {
            totalStreams: webrtc.remoteStreams.value.size,
            streamKeys: Array.from(webrtc.remoteStreams.value.keys())
          })

          // Attach remote audio for each stream (for per-player mute and level monitoring)
          webrtc.remoteStreams.value.forEach((stream, playerId) => {
            if (stream.getAudioTracks().length > 0) {
              audio.attachRemoteAudio(playerId, stream)
            }
          })

          // Detach audio for removed streams
          audio.remoteAudioStates.value.forEach((_, playerId) => {
            if (!webrtc.remoteStreams.value.has(playerId)) {
              audio.detachRemoteAudio(playerId)
            }
          })
        })
      },
      { deep: true }
    )
  }

  // ============================================================================
  // Setup Function - Call after player computed props are defined
  // ============================================================================

  /**
   * Configure the composable with player computed properties.
   * Must be called after defining partnerPlayer, opponent1Player, opponent2Player.
   */
  function setup(options: UseGameMediaOptions): void {
    partnerPlayer.value = options.partnerPlayer
    opponent1Player.value = options.opponent1Player
    opponent2Player.value = options.opponent2Player

    // Setup the remote stream watcher
    setupRemoteStreamWatcher()
  }

  return {
    // Refs
    localVideoRef,
    partnerVideoRef,
    opponent1VideoRef,
    opponent2VideoRef,

    // Computed
    partnerHasVideo,
    opponent1HasVideo,
    opponent2HasVideo,

    // Underlying composables (exposed for template access)
    video,
    audio,
    webrtc,

    // Methods
    handleVideoToggle,
    handleAudioToggle,
    handleResumeAudio,
    hasRemoteVideo,
    bindRemoteVideo,
    onVideoMounted,

    // Setup
    setup
  }
}
